/**
 * Utility to generate SEO data from routes configuration
 * This is the single source of truth for SEO metadata across the application
 */
import routes, { ROUTE_TYPES } from "routes/index";

const BASE_URL = "https://www.kalyanawaran.com";

/**
 * Build SEO_PAGES object from routes configuration
 * This eliminates duplication between routes and seoConstants
 */
export const buildSeoPages = () => {
  const seoPages = {};

  // Get all routes from all layouts
  const allRoutes = [
    ...(routes.blankLayout || []),
    ...(routes.defaultLayout || []),
  ];

  // Map routes to SEO pages using a key based on path
  allRoutes.forEach((route) => {
    if (!route.path || route.path === "*") return; // Skip catch-all routes

    // Create a unique key from the path for the SEO_PAGES object
    const key = getRouteKeyFromPath(route.path);

    // Only add if we have title and description
    if (route.title && route.description) {
      seoPages[key] = {
        title: route.title,
        description: route.description,
        link: `${BASE_URL}${getNormalizedPath(route.path)}`,
      };
    }
  });

  return seoPages;
};

/**
 * Get SEO data for a specific path
 * @param {string} path - The route path (e.g., '/profileedit', '/search-profiles')
 * @returns {Object|null} - SEO object {title, description, link} or null if not found
 */
export const getRouteByPath = (path) => {
  const allRoutes = [
    ...(routes.blankLayout || []),
    ...(routes.defaultLayout || []),
  ];

  // Normalize path for matching (remove trailing slash)
  const normalizedPath = path.replace(/\/$/, "");

  const route = allRoutes.find((r) => {
    const routePath = r.path.replace(/\/$/, "");
    return routePath === normalizedPath;
  });

  if (!route || !route.title || !route.description) {
    return null;
  }

  return {
    title: route.title,
    description: route.description,
    link: `${BASE_URL}${getNormalizedPath(route.path)}`,
  };
};

/**
 * Get route by screen name (used by analytics tracker)
 * @param {string} screenName - The analytics screen name
 * @returns {Object|null} - Route object or null if not found
 */
export const getRouteByScreenName = (screenName) => {
  const allRoutes = [
    ...(routes.blankLayout || []),
    ...(routes.defaultLayout || []),
  ];

  return allRoutes.find((r) => r.screen === screenName) || null;
};

/**
 * Convert a route path to a SEO_PAGES key
 * Examples:
 *   '/profileedit' -> 'PROFILE_EDIT'
 *   '/search-profiles' -> 'SEARCH_PROFILES'
 *   '/members/:id' -> 'MEMBERS_PAGE'
 */
function getRouteKeyFromPath(path) {
  const pathMap = {
    "/": "HOME",
    "/register": "REGISTER",
    "/login": "LOGIN",
    "/profileedit": "PROFILE_EDIT",
    "/forgetpassword": "FORGET_PASSWORD",
    "/search-profiles": "SEARCH_PROFILES",
    "/members/:id": "MEMBERS_PAGE",
    "/terms": "TERMS",
    "/privacy-policy": "PRIVACY_POLICY",
    "/cookie-policy": "COOKIE_POLICY",
    "/preference": "PREFERENCES",
    "/resetPassword/:token": "RESET_PASSWORD",
    "/emailVerification/:token": "EMAIL_VERIFICATION",
    "/contact-us": "CONTACT_US",
    "/about-us": "ABOUT_US",
    "/nakshatra-rashi-list": "NAKSHATRA_RASHI",
    "/quick-upload": "QUICK_UPLOAD",
    "/gotralu": "GOTRALU",
    "/social-groups": "SOCIAL_GROUPS",
    "/account": "ACCOUNT_INFORMATION",
    "/dashboard/*": "DASHBOARD",
  };

  return pathMap[path] || path.replace(/[^a-zA-Z0-9]/g, "_").toUpperCase();
}

/**
 * Normalize a route path by removing dynamic segments
 * Examples:
 *   '/members/:id' -> '/members'
 *   '/resetPassword/:token' -> '/resetPassword'
 *   '/dashboard/*' -> '/dashboard'
 */
function getNormalizedPath(path) {
  return path
    .replace(/:.*?($|\/)/, "") // Remove :param
    .replace(/\*$/, ""); // Remove trailing *
}

export default buildSeoPages;
